/**
 * Settings overlay (06 S14–S19). A small internal router over uiStore's
 * settings `page`. Covers root, profile, privacy, notifications, appearance,
 * security/encryption, and linked-devices (read-only v1).
 */
import { useState } from 'react';
import {
  Lock,
  Bell,
  Palette,
  ShieldCheck,
  Smartphone,
  KeyRound,
  HelpCircle,
  LogOut,
} from 'lucide-react';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore, type SettingsPage } from '../../stores/uiStore';
import { useThemeStore, type ThemePref } from '../../stores/themeStore';
import { Avatar } from '../primitives/Avatar';
import { OverlayPanel } from '../primitives/OverlayPanel';
import { RadioGroup, SettingsRow, Toggle } from './SettingsControls';
import styles from './Settings.module.css';

export function Settings({ page }: { page: SettingsPage }) {
  const setLeftOverlay = useUiStore((s) => s.setLeftOverlay);
  const closeLeftOverlay = useUiStore((s) => s.closeLeftOverlay);
  const go = (p: SettingsPage) => setLeftOverlay({ kind: 'settings', page: p });

  switch (page) {
    case 'profile':
      return <ProfilePage onBack={() => go('root')} />;
    case 'privacy':
      return <PrivacyPage onBack={() => go('root')} />;
    case 'notifications':
      return <NotificationsPage onBack={() => go('root')} />;
    case 'appearance':
      return <AppearancePage onBack={() => go('root')} />;
    case 'security':
      return <SecurityPage onBack={() => go('root')} />;
    case 'devices':
      return <DevicesPage onBack={() => go('root')} />;
    case 'root':
    default:
      return <SettingsRoot onBack={closeLeftOverlay} go={go} />;
  }
}

function SettingsRoot({ onBack, go }: { onBack: () => void; go: (p: SettingsPage) => void }) {
  const me = useChatStore((s) => s.me);
  return (
    <OverlayPanel title="Settings" onBack={onBack}>
      <button className={styles.profileHeader} onClick={() => go('profile')}>
        <Avatar id={me.id} name={me.displayName} url={me.avatarUrl} size={64} />
        <span className={styles.profileText}>
          <span className={styles.profileName}>{me.displayName}</span>
          <span className={styles.profileAbout}>{me.about}</span>
        </span>
      </button>
      <SettingsRow icon={<KeyRound size={20} />} label="Account" value="Security notifications, change number" onClick={() => go('security')} />
      <SettingsRow icon={<Lock size={20} />} label="Privacy" value="Last seen, read receipts" onClick={() => go('privacy')} />
      <SettingsRow icon={<Bell size={20} />} label="Notifications" value="Message tones, push" onClick={() => go('notifications')} />
      <SettingsRow icon={<Palette size={20} />} label="Appearance" value="Theme, wallpaper, font size" onClick={() => go('appearance')} />
      <SettingsRow icon={<ShieldCheck size={20} />} label="Encryption" value="Security code, verify contacts" onClick={() => go('security')} />
      <SettingsRow icon={<Smartphone size={20} />} label="Linked devices" onClick={() => go('devices')} />
      <SettingsRow icon={<HelpCircle size={20} />} label="Help" value="Help center, contact us" />
      <SettingsRow icon={<LogOut size={20} />} label="Log out" />
    </OverlayPanel>
  );
}

function ProfilePage({ onBack }: { onBack: () => void }) {
  const me = useChatStore((s) => s.me);
  return (
    <OverlayPanel title="Profile" onBack={onBack}>
      <div className={styles.avatarBlock}>
        <Avatar id={me.id} name={me.displayName} url={me.avatarUrl} size={200} />
      </div>
      <Field label="Your name" value={me.displayName} />
      <Field label="About" value={me.about} />
      <Field label="Handle" value={`@${me.handle}`} readOnly />
    </OverlayPanel>
  );
}

function Field({ label, value, readOnly }: { label: string; value: string; readOnly?: boolean }) {
  const [v, setV] = useState(value);
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        className={styles.fieldInput}
        value={v}
        onChange={(e) => setV(e.target.value)}
        readOnly={readOnly}
        aria-label={label}
      />
    </div>
  );
}

function PrivacyPage({ onBack }: { onBack: () => void }) {
  const [lastSeen, setLastSeen] = useState<'everyone' | 'contacts' | 'nobody'>('contacts');
  const [readReceipts, setReadReceipts] = useState(true);
  return (
    <OverlayPanel title="Privacy" onBack={onBack}>
      <h2 className={styles.section}>Last seen and online</h2>
      <RadioGroup
        name="Last seen"
        value={lastSeen}
        onChange={setLastSeen}
        options={[
          { value: 'everyone', label: 'Everyone' },
          { value: 'contacts', label: 'My contacts' },
          { value: 'nobody', label: 'Nobody' },
        ]}
      />
      <SettingsRow
        label="Read receipts"
        value="If turned off, you won't send or receive read receipts."
        trailing={<Toggle checked={readReceipts} onChange={setReadReceipts} label="Read receipts" />}
      />
    </OverlayPanel>
  );
}

function NotificationsPage({ onBack }: { onBack: () => void }) {
  const [sounds, setSounds] = useState(true);
  const [push, setPush] = useState(true);
  return (
    <OverlayPanel title="Notifications" onBack={onBack}>
      <h2 className={styles.section}>Messages</h2>
      <SettingsRow label="Notification tones" trailing={<Toggle checked={sounds} onChange={setSounds} label="Tones" />} />
      <SettingsRow
        label="Web push"
        value="Show notifications when the app is in the background"
        trailing={<Toggle checked={push} onChange={setPush} label="Web push" />}
      />
      <p className={styles.note}>
        Push payloads carry metadata only (who & chat). Message content is
        decrypted on this device when you open the chat.
      </p>
    </OverlayPanel>
  );
}

function AppearancePage({ onBack }: { onBack: () => void }) {
  const pref = useThemeStore((s) => s.pref);
  const setPref = useThemeStore((s) => s.setPref);
  return (
    <OverlayPanel title="Appearance" onBack={onBack}>
      <h2 className={styles.section}>Theme</h2>
      <RadioGroup<ThemePref>
        name="Theme"
        value={pref}
        onChange={setPref}
        options={[
          { value: 'light', label: 'Light' },
          { value: 'dark', label: 'Dark' },
          { value: 'system', label: 'System default' },
        ]}
      />
      <h2 className={styles.section}>Chat wallpaper</h2>
      <SettingsRow label="Default doodle wallpaper" value="Tap to change (demo)" onClick={() => {}} />
    </OverlayPanel>
  );
}

function SecurityPage({ onBack }: { onBack: () => void }) {
  return (
    <OverlayPanel title="Encryption" onBack={onBack}>
      <div className={styles.securityHero}>
        <ShieldCheck size={48} className={styles.shield} />
        <p>
          Your messages and calls are secured with end-to-end encryption, so
          they stay between you and the people you choose.
        </p>
      </div>
      <h2 className={styles.section}>Security code</h2>
      <div className={styles.code}>
        12345 67890 12345 67890 12345 67890 12345 67890 12345 67890 12345 67890
      </div>
      <p className={styles.note}>
        Scan the QR code on your contact's phone, or compare these 60 digits, to
        verify your conversation is end-to-end encrypted.
      </p>
    </OverlayPanel>
  );
}

function DevicesPage({ onBack }: { onBack: () => void }) {
  return (
    <OverlayPanel title="Linked devices" onBack={onBack}>
      <p className={styles.note}>
        Sinyal v1 is single-device. Message history does not transfer to a new
        device — a freshly linked device starts with an empty history.
      </p>
      <SettingsRow icon={<Smartphone size={20} />} label="This device (web)" value="Active now" />
    </OverlayPanel>
  );
}
